<template>
  <d-card>
    <v-form
      ref="form"
      :data="form"
      format="formData"
      :url="endpoint.url"
      :form-data-options="{
        nullsAsUndefineds: false,
        booleansAsIntegers: true,
        allowEmptyArrays: true,
        indices: true,
      }"
      :method="endpoint.method"
      :before-submit="beforeSubmit"
      @dirty="$emit('dirty')"
      @success="gotToPropertyDetails($event)"
      @error="parseErrors"
    >
      <template #default="{ errors }">
        <d-card-row
          v-if="!$roleCan([$laravel.role_client]) || $laravel.user.is_owner_contact
          "
        >
          <v-input-autocomplete-fetch
            required
            :value="owner"
            :route="$laravel.route('api.owners.index')"
            name="owner_id"
            field="name"
            :placeholder="$t('owner').capitalize()"
            :label="$t('owner').capitalize()"
            open-on-focus
            @input="onOwnerChange($event)"
          />
        </d-card-row>
        <d-card-row v-if="isShowOwnerBusiness">
          <v-input-autocomplete-fetch
            ref="autocompleteFetch"
            :has-info="true"
            class="custom-tooltip-position autocomplete-fetch-with-footer"
            info-size="is-large"
            info-type="html"
            :has-action="true"
            :append-to-body="false"
            required
            :value="ownerBusiness"
            :route="$laravel.route('api.owner-businesses.index', {
              owner: form.owner_id,
            })
            "
            name="owner_business_id"
            field="name"
            :placeholder="$t('owner_businesses').capitalize()"
            :label="$t('owner_businesses').capitalize()"
            open-on-focus
            @input="onOwnerBusinessChange($event)"
          >
            <template #info>
              <p class="has-text-left">
                {{ $t("business_entity_input_info") }}
              </p>
            </template>
            <template #action>
              <a
                v-if="canCreateLegalEntity"
                role="button"
                class="footer-item"
                @click="toggleOwnerBusinessEntityModal()"
              >
                <span> {{ $t("new_owner_business") }} </span>
              </a>
            </template>
          </v-input-autocomplete-fetch>
        </d-card-row>

        <d-card-row>
          <v-input-select
            v-model="form.region_id"
            name="region_id"
          >
            <option value="">
              {{ $t("select").capitalize() }}
            </option>

            <option
              v-for="(region, index) in $laravel.propertyRegions"
              :key="index"
              :value="region.id"
            >
              {{ region.name }}
            </option>
          </v-input-select>
        </d-card-row>

        <d-card-row>
          <v-input-select
            v-model="form.type"
            name="type"
            :label="$t('housing_size')"
            required
          >
            <option value="">
              {{ $t("select").capitalize() }}
            </option>
            <option
              v-for="(type, key) in $laravel.propertyTypes"
              :key="key"
              :value="type"
            >
              {{ $t(type) }}
            </option>
          </v-input-select>
        </d-card-row>

        <d-card-row>
          <div
            ref="inclusionsContainerRef"
            class="is-flex is-align-items-center"
          >
            <button
              type="button"
              class="button"
              @click="isModalInclusionsVisible = !isModalInclusionsVisible"
            >
              <div class="icon">
                <i class="mdi mdi-tag mdi-18px" />
              </div>
              <span>
                {{ $t("inclusion_list").capitalize() }} ({{ activeInclusions }})
              </span>
            </button>
            <div class="ml-3">
              <v-input-switch
                v-model="form.is_empty_inclusions"
                :true-value="true"
                :false-value="false"
                label-position="right"
                name="is_empty_inclusions"
              />
            </div>
          </div>
          <div
            v-if="showInclusionsErrors"
            class="mt-4"
          >
            <b-notification
              :closable="false"
              type="is-danger is-light"
              :aria-close-label="$t('close')"
              role="alert"
            >
              {{ inclusionsErrorMsg }}
            </b-notification>
          </div>
        </d-card-row>
        <d-card-row>
          <div ref="landryRoomAndWasherAndDryerEntryContainer">
            <div class="mb-3">
              <v-input-select
                v-model="form.has_laundry_room"
                name="has_laundry_room"
                required
              >
                <option :value="null">
                  {{ $t("select").capitalize() }}
                </option>

                <option :value="true">
                  {{ $t("yes").capitalize() }}
                </option>

                <option :value="false">
                  {{ $t("no").capitalize() }}
                </option>
              </v-input-select>
            </div>
            <div>
              <v-input-select
                v-model="form.has_washer_and_dryer_entry"
                name="has_washer_and_dryer_entry"
                required
              >
                <option :value="null">
                  {{ $t("select").capitalize() }}
                </option>

                <option :value="true">
                  {{ $t("yes").capitalize() }}
                </option>

                <option :value="false">
                  {{ $t("no").capitalize() }}
                </option>
              </v-input-select>
            </div>
          </div>
        </d-card-row>

        <b-modal
          v-model="isModalInclusionsVisible"
          v-bind="form.inclusionsList"
          :width="600"
          @close="isModalInclusionsVisible = !isModalInclusionsVisible"
        >
          <div class="modal-card">
            <header
              class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
            >
              {{ $t("inclusion_list").capitalize() }}
            </header>
            <section class="modal-card-body">
              <v-inclusions
                :inclusions="form.inclusionsList"
                @updated="setInclusionsData"
              />
            </section>
            <footer class="modal-card-foot">
              <b-button
                :label="$t('close')"
                @click="isModalInclusionsVisible = !isModalInclusionsVisible"
              />
              <button
                class="button is-primary"
                type="button"
                @click="isModalInclusionsVisible = !isModalInclusionsVisible"
              >
                {{ $t("save") }}
              </button>
            </footer>
          </div>
        </b-modal>

        <d-card-row>
          <v-input
            v-model="form.square_feet_area"
            type="number"
            required
            name="square_feet_area"
          />
        </d-card-row>

        <d-card-row>
          <v-input
            v-model="form.floor_number"
            required
            type="text"
            name="floor_number"
          />
        </d-card-row>

        <d-card-row>
          <v-input
            v-model="form.number_of_balconies"
            type="number"
            :min="0"
            name="number_of_balconies"
          />
        </d-card-row>

        <d-card-row>
          <v-input-select
            v-model="form.dog_tolerance"
            name="dog_tolerance"
            required
          >
            <option :value="null">
              {{ $t("select").capitalize() }}
            </option>

            <option :value="true">
              {{ $t("yes").capitalize() }}
            </option>

            <option :value="false">
              {{ $t("no").capitalize() }}
            </option>
          </v-input-select>

          <v-input-select
            v-model="form.cat_tolerance"
            name="cat_tolerance"
            required
          >
            <option :value="null">
              {{ $t("select").capitalize() }}
            </option>

            <option :value="true">
              {{ $t("yes").capitalize() }}
            </option>

            <option :value="false">
              {{ $t("no").capitalize() }}
            </option>
          </v-input-select>

          <v-input-rich-text
            v-model="form.tolerance_notes"
            name="tolerance_notes"
          />
        </d-card-row>

        <v-form-address
          v-model="form.address"
          :title="$t('address')"
          :prefix="'address.'"
          :errors="errors"
        />
        <d-card-row v-if="hasAddressDuplicated">
          <b-notification
            type="is-danger"
            has-icon
            :closable="false"
            role="alert"
          >
            {{ addressDuplicatedText.capitalize() }}

            <router-link
              v-if="duplicatedProperty"
              :to="`/properties/${duplicatedProperty.id}/details`"
            >
              {{ $t('duplicated_property_address_text') }}
            </router-link>
          </b-notification>
        </d-card-row>

        <d-card-row>
          <button class="button is-primary">
            <span v-if="action === 'edit'">
              {{ $t("save") }}
            </span>
            <span v-else>
              {{ $t("next") }}
            </span>
          </button>
        </d-card-row>
      </template>
    </v-form>

    <b-modal
      v-model="isShowAddBusinessEntityModal"
      :width="600"
      @close="isShowAddBusinessEntityModal = false"
    >
      <div class="modal-card">
        <header
          class="modal-card-head is-flex-direction-column is-flex is-align-items-flex-start"
        >
          <div
            class="is-flex is-full pb-4 is-align-content-space-between"
            style="width: 100%"
          >
            <p class="modal-card-title title mb-0">
              {{ $t("new_owner_business") }}
            </p>
            <button
              class="delete"
              aria-label="close"
              @click="isShowAddBusinessEntityModal = false"
            />
          </div>
        </header>

        <section
          v-if="owner"
          class="modal-card-body"
          style="padding-left: 0; padding-right: 0"
        >
          <v-form
            ref="owner_business_entity_form"
            :data="formOwnerEntity"
            :url="$laravel.route('api.owner-businesses.store', owner.id)"
            method="post"
            @success="successAddBusinessEntityMethod"
          >
            <template #default="{ errors }">
              <d-card-row>
                <v-input
                  v-model="formOwnerEntity.name"
                  name="name"
                />
              </d-card-row>
              <v-form-address
                v-model="formOwnerEntity.billing_address"
                :title="$t('business_entity_address_label')"
                :prefix="'billing_address.'"
                :errors="errors"
              />

              <d-card-row>
                <div class="is-flex is-justify-content-flex-end">
                  <button
                    class="button is-primary"
                    type="submit"
                  >
                    {{ $t("save") }}
                  </button>
                </div>
              </d-card-row>
            </template>
          </v-form>
        </section>
      </div>
    </b-modal>
  </d-card>
</template>

<script>
import { fillFields } from "@/services/helpers";
import address from "@/shared/components/address/form.vue";
import inclusions from "@/admin/views/propertyStatus/_inclusions.vue";

import { uniqueId } from "lodash";
import { i18n } from "@/services/i18n";

const DEFAULT_OWNER_FORM = {
  name: "",
  billing_address: {
    city: "",
    country: "ca",
    address_1: "",
    address_2: "",
    province: "qc",
    postal_code: "",
  },
};

export default {
  components: {
    "v-form-address": address,
    "v-inclusions": inclusions,
  },
  props: {
    action: {
      type: String,
      required: true,
    },
    model: {
      default: null,
      type: Object,
    },
  },
  data() {
    return {
      formOwnerEntity: _.cloneDeep(DEFAULT_OWNER_FORM),
      owner: this.model && this.model.owner ? this.model.owner : null,
      isModalInclusionsVisible: false,
      form: {
        owner_id: this.$laravel.user.owner_id,
        type: "",
        floor_number: "",
        number_of_balconies: 0,
        square_feet_area: "",
        region_id: "",
        dog_tolerance: null,
        cat_tolerance: null,
        tolerance_notes: "",
        address: {
          city: "",
          country: "ca",
          address_1: "",
          address_2: "",
          province: "qc",
          postal_code: "",
        },
        inclusions: null,
        inclusionsList: [],
        owner_business_id: null,
        is_empty_inclusions: false,
        has_laundry_room:null,
        has_washer_and_dryer_entry:null,
      },
      isShowAddBusinessEntityModal: false,
      isShowOwnerBusiness:
        (this.model && this.model.owner) || this.$laravel.user.owner_id
          ? true
          : false,
      hasAddressDuplicated: false,
      addressDuplicatedText: null,
      ownerBusiness:
        this.model && this.model.owner_business
          ? this.model.owner_business
          : null,
      activeInclusions: 0,
      showInclusionsErrors: false,
      inclusionsErrorMsg: "",
      duplicatedProperty: null,
    };
  },
  computed: {
    endpoint() {
      if (this.action === "create" || this.action === "reset") {
        return {
          method: "post",
          url: this.$laravel.route("api.properties.store"),
        };
      }
      if (this.action === "edit") {
        return {
          method: "patch",
          url: this.$laravel.route("api.properties.update", {
            property: this.model.id,
          }),
        };
      }
      return null;
    },
    canCreateLegalEntity()
    {
      return window.Laravel.user.permissions.ownerBusiness.create;
    }
  },
  created() {
    if (this.action === "edit" || this.action == "reset") {
      this.form = fillFields(this.form, this.model);
      const inclusions = this.model.inclusions.map((inclusion) => {
        return {
          id: inclusion.id,
          status: inclusion.status,
          inclusion_label: inclusion.inclusion_label,
          notes: inclusion.notes,
        };
      });
      this.form.inclusionsList = inclusions;

      this.countActiveInclusion();

      if (this.activeInclusions > 0) {
        this.form.is_empty_inclusions = false;
      } else {
        this.form.is_empty_inclusions = true;
      }
    }
    if (this.action === "create") {
      const inclusionsList = [];
      this.$laravel.inclusions.map((inclusion) => {
        inclusionsList.push({
          id: uniqueId(),
          inclusion: inclusion.key,
          inclusion_label: inclusion.inclusion,
          status: false,
          notes: "",
        });
      });
      this.form.inclusionsList = inclusionsList;
      this.form.inclusions = inclusionsList;
      if (this.$laravel.user.owner_id) {
        this.getOwnerData();
      }
    }

    const address = this.$route.query.address;
    const unit = this.$route.query.unit;
    const city = this.$route.query.city;
    const postal_code = this.$route.query.postal_code;
    const province = this.$route.query.province;


    if (address) {
      this.form.address.address_1 = address;
    }

    if (unit) {
      this.form.address.address_2 = unit;
    }

    if (city) {
      this.form.address.city = city;
    }

    if (postal_code) {
      this.form.address.postal_code = postal_code;
    }

    if (province) {
      this.form.address.province = province;
    }

    if (country) {
      this.form.address.province = country;
    }

  },
  methods: {
    beforeSubmit(query) {
      let hasErrors = false;

      if (this.activeInclusions && this.form.is_empty_inclusions) {
        this.showInclusionsErrors = true;
        this.inclusionsErrorMsg = i18n.t("inclusion_list_error_has_inclusions");
        hasErrors = true;
      }

      if (this.activeInclusions == 0 && !this.form.is_empty_inclusions) {
        this.inclusionsErrorMsg = i18n.t(
          "inclusion_list_error_has_not_inclusions"
        );
        this.showInclusionsErrors = true;
        hasErrors = true;
      }

      if (hasErrors) {
        this.$refs.inclusionsContainerRef.scrollIntoView({
          behavior: "smooth",
        });
        return;
      }

      if (!hasErrors) {
        this.showInclusionsErrors = false;
        this.inclusionsErrorMsg = "";
        query();
      }
    },
    parseErrors(error) {
      if (error.response.data.errors && error.response.data.errors["address"]) {
        this.hasAddressDuplicated = true;
        this.addressDuplicatedText = error.response.data.errors["address"][0];

        this.getDuplicatedProperty();

        return;
      }
      this.hasAddressDuplicated = false;
      this.addressDuplicatedText = "";
      this.duplicatedProperty = null;
    },
    cancelCreateProperty() {
      this.$router.push("/properties");
    },
    setInclusionsData(id, key, value) {
      const index = this.form.inclusions.map((object) => object.id).indexOf(id);
      this.form.inclusions[index][key] = value;
      this.countActiveInclusion();
    },
    countActiveInclusion() {
      const countActiveInclusion = this.form.inclusions.filter(
        (object) => object.status == true
      );

      if (
        Array.isArray(countActiveInclusion) &&
        countActiveInclusion.length > 0
      ) {
        this.activeInclusions = countActiveInclusion.length;
      } else {
        this.activeInclusions = 0;
      }
    },
    onOwnerChange(owner) {
      if (owner && owner.id) {
        this.form.owner_id = owner.id;
        this.owner = owner;
        this.isShowOwnerBusiness = true;
      } else {
        this.form.owner_id = null;
        this.owner = null;
        this.isShowOwnerBusiness = false;
      }
    },
    onOwnerBusinessChange(ownerBusiness) {
      if (ownerBusiness && ownerBusiness.id) {
        this.form.owner_business_id = ownerBusiness.id;
        this.ownerBusiness = ownerBusiness;
      } else {
        this.form.owner_business_id = null;
        this.ownerBusiness = null;
      }
    },
    gotToPropertyDetails(event) {
      if (event) {
        const property_id = event.data.property.id;
        if (this.action === "create" || this.action === "reset") {
          this.$router.push("/properties/create/" + property_id + "/pictures");
        } else {
          this.$router.push({
            name: "properties.show.details",
            params: { property: property_id },
          });
        }
      }
    },
    toggleOwnerBusinessEntityModal() {
      if (!this.isShowAddBusinessEntityModal) {
        this.$refs.autocompleteFetch.$refs.autocompleteFetch.$refs.autocompleteFetch.setSelected(
          null
        );
      }
      this.isShowAddBusinessEntityModal = !this.isShowAddBusinessEntityModal;
    },
    successAddBusinessEntityMethod(response) {
      this.ownerBusiness = response.data.ownerBusiness;
      this.form.owner_business_id = response.data.ownerBusiness.id;
      this.formOwnerEntity = _.cloneDeep(DEFAULT_OWNER_FORM);
      this.toggleOwnerBusinessEntityModal();
    },
    getOwnerData() {
      this.loading = true;
      this.$http
        .get(
          this.$laravel.route("api.owners.show", {
            owner: this.$laravel.user.owner_id,
          })
        )
        .then((response) => {
          this.loading = false;
          this.owner = response.data.data;
        })
        .catch((e) => {
          console.error(e);
        });
    },
    getDuplicatedProperty()
    {
      const data = new FormData;

      data.append("address_1", this.form.address["address_1"]);
      data.append("address_2", this.form.address["address_2"]);
      data.append("owner_id", this.form.owner_id);
      data.append("property_id", this.model ? this.model.id : null);

      console.log(data);
      //api/properties/duplicated
      this.$http
        .post(
          this.$laravel.route("api.properties.duplicated"),
          data
        )
        .then((response) => {
          this.duplicatedProperty = response.data.property;
        })
        .catch((e) => {
          console.error(e);
          this.duplicatedProperty = null;
        });
    }
  },
};
</script>
<style>
.autocomplete .dropdown-footer:hover {
  background-color: hsl(220, 14%, 96%);
  color: hsl(216, 100%, 4%);
}
.autocomplete .dropdown-footer a {
  color: #3f51b5;
}
</style>
